import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  FormControlLabel, Switch, Autocomplete, Alert, Divider,
} from '@mui/material';
import { useApp } from '../context/AppContext';

export default function FeatureDetailPage() {
  const navigate = useNavigate();
  const { projectId, flagId } = useParams<{ projectId: string; flagId: string }>();
  const { flags, currentProject, updateFlag } = useApp();

  const flag = flags.find(f => f.flagId === flagId);
  const envKeys = currentProject?.environments?.sort((a, b) => a.order - b.order).map(e => e.key) || [];

  const [name, setName] = useState(flag?.name || '');
  const [userDisplayMessage, setUserDisplayMessage] = useState(flag?.userDisplayMessage || '');
  const [masterActive, setMasterActive] = useState(flag?.masterActive ?? false);
  const [dependencies, setDependencies] = useState<string[]>(flag?.dependencies || []);
  const [envStates, setEnvStates] = useState<Record<string, boolean>>(flag?.environments || {});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (flag) {
      setName(flag.name);
      setUserDisplayMessage(flag.userDisplayMessage);
      setMasterActive(flag.masterActive);
      setDependencies(flag.dependencies);
      setEnvStates(flag.environments);
    }
  }, [flag]);

  const existingKeys = flags.filter(f => f.flagId !== flagId).map(f => f.key);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flagId) return;
    setError(null);
    setSaving(true);
    try {
      await updateFlag(flagId, { name, userDisplayMessage, masterActive, dependencies, environments: envStates });
      navigate(`/projects/${projectId}/flags`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update flag');
    } finally {
      setSaving(false);
    }
  };

  if (!flag) return <Typography>Flag not found</Typography>;

  return (
    <Box maxWidth={600}>
      <Typography variant="h5" fontWeight={700} mb={1}>{flag.key}</Typography>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ fontFamily: 'monospace' }}>
        {flag.flagId}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth label="Display Name" value={name}
              onChange={e => setName(e.target.value)}
              margin="normal" required
            />
            <TextField
              fullWidth label="User Display Message" value={userDisplayMessage}
              onChange={e => setUserDisplayMessage(e.target.value)}
              margin="normal" multiline rows={2}
            />
            <Autocomplete
              multiple freeSolo
              options={existingKeys}
              value={dependencies}
              onChange={(_, val) => setDependencies(val as string[])}
              renderInput={params => (
                <TextField {...params} label="Dependencies" margin="normal" />
              )}
            />
            <FormControlLabel
              control={<Switch checked={masterActive} onChange={e => setMasterActive(e.target.checked)} />}
              label="Master Active"
              sx={{ mt: 1 }}
            />
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" fontWeight={600} mb={2}>Environments</Typography>
            {envKeys.map(k => (
              <FormControlLabel
                key={k}
                control={
                  <Switch
                    checked={envStates[k] ?? false}
                    onChange={e => setEnvStates(prev => ({ ...prev, [k]: e.target.checked }))}
                    disabled={!masterActive}
                  />
                }
                label={currentProject?.environments.find(e => e.key === k)?.name || k}
                sx={{ display: 'flex', mb: 1 }}
              />
            ))}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button onClick={() => navigate(`/projects/${projectId}/flags`)}>Cancel</Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
